import React, { useState } from "react";
import { GlobalApplicationState } from "globalApplicationState";
import { connect, ConnectedProps } from "react-redux";
import { push } from "react-router-redux";

import TabContent from "pages/common/tabContent";
import { ISMS, ISMSFilters, ISMSListingPage, ISMSListItem } from "./models";
import SMSFilters from "./smsFilters";
import SMSTable from "./smsTable";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { smsApi } from "api/instances";
import SMSPreview from "./smsPreview";
import cookie, { ROWS_PER_PAGE_COOKIE_NAMES } from "utils/cookie";
import ConfirmDialog from "modules/common/components/dialogs/confirmDialog";
import LoadingOverlay from "modules/common/components/loadingOverlay";

interface ISMSListProps {
    show: boolean;
    filters: ISMSFilters;
    onUpdateFilters: (newFilters: ISMSFilters) => void;
    fetchPage: (filters?: ISMSFilters, pageNumber?: number, pageAmount?: number) => void;
    fetchAllPages: () => void;
    onDownloadSMSActivity: (smsId: string) => void;
    defaultSMSFilterValues: ISMSFilters;
    page: ISMSListingPage;
}

const SMSList: React.FC<IConnectedSMSListProps> = ({
    show,
    filters,
    page,
    tenantId,
    defaultSMSFilterValues,
    redirectTo,
    onUpdateFilters,
    fetchPage,
    fetchAllPages,
    onDownloadSMSActivity
}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUnsending, setIsUnsending] = useState(false);
    const showIsLoading = page.isFetching || isDeleting || isUnsending;
    const hasFiltersApplied = filters.textToSearch !== defaultSMSFilterValues.textToSearch;
    const [showDeleteSMSDialog, setShowDeleteSMSDialog] = useState(false);
    const [showUnsendSMSDialog, setShowUnsendSMSDialog] = useState(false);
    const [smsIdToDelete, setSMSIdToDelete] = useState("");
    const [smsIdToUnsend, setSMSIdToUnsend] = useState("");
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [smsToPreview, setSMSToPreview] = useState<ISMS | undefined>();

    const { setErrorMessage, setInfoMessage, setSuccessMessage } = useSetSnackbarMessages();

    const onChangeFilters = (newFilters: ISMSFilters) => {
        const rowsPerPage = cookie.getRowsPerPage(ROWS_PER_PAGE_COOKIE_NAMES.SMS, 25);

        onUpdateFilters(newFilters);
        onChangePage(0, rowsPerPage, newFilters);
    }

    const handleDraftSMSClick = (sms: ISMSListItem) => redirectTo("/" + tenantId + "/admin/sms/edit/" + sms.id);

    const onChangePage = (page: number,  rowsPerPage: number, filters: ISMSFilters) => {
        fetchPage(filters, page + 1, rowsPerPage);
    }

    const onPreviewSMS = async (smsToDisplay: ISMSListItem) => {
        setIsLoadingPreview(true);
        
        try {
            const { data } = await smsApi.getSMS(smsToDisplay.id);
            setSMSToPreview(data);
        } catch (error) {
            setSMSToPreview(undefined);
            setErrorMessage("There was an error with your request. Please try again later.");
        } finally {
            setIsLoadingPreview(false);
        }
    }

    const onDeleteSMS = (sms: ISMSListItem) => {
        setSMSIdToDelete(sms.id);
        setShowDeleteSMSDialog(true);
    }

    const deleteSMS = async () => {
        setShowDeleteSMSDialog(false);
        setIsDeleting(true);
        try {
            await smsApi.deleteSMS(smsIdToDelete);
            fetchPage();
            setInfoMessage("SMS deleted.");
        }
        catch {
            setErrorMessage("There was an error deleting the SMS. Please try again later.");
        }
        finally {
            resetDeleteSMSStates();
        }
    }

    const unsendSMS = async () => {
        setShowUnsendSMSDialog(false);
        try {
            setIsUnsending(true);
            await smsApi.cancelSend(smsIdToUnsend);
            fetchAllPages();
            setSuccessMessage("Your SMS was cancelled and is now in drafts.");
        }
        catch {
            setErrorMessage("There was an error with cancelling the SMS. Please try again later");
        }
        finally {
            resetUnsendSMSStates();
        }
    }

    const onDuplicateSMS = async (sms: ISMSListItem) => {
        try {
            const { data } = await smsApi.cloneSMS(sms.id);
            redirectTo("/" + tenantId + "/admin/sms/edit/" + data);
        }
        catch { 
            setErrorMessage("There was an error with duplicating. Please try again later.");
        }
    }

    const onUnsendSMS = (sms: ISMSListItem) => {
        setSMSIdToUnsend(sms.id);
        setShowUnsendSMSDialog(true);
    }

    const resetDeleteSMSStates = () => {
        setShowDeleteSMSDialog(false);
        setSMSIdToDelete("");
        setIsDeleting(false);
    }

    const resetUnsendSMSStates = () => {
        setShowUnsendSMSDialog(false);
        setSMSIdToUnsend("");
        setIsUnsending(false);
    }

    return !show
        ? <></>
        : <React.Fragment>
            <TabContent>
                <SMSFilters
                    filters={filters}
                    onChangeFilters={onChangeFilters}
                    onClearFilters={() => onChangeFilters(defaultSMSFilterValues)}
                />
                <SMSTable
                    show={show}
                    showIsLoading={showIsLoading}
                    page={page}
                    filters={filters}
                    hasFiltersApplied={hasFiltersApplied}
                    onChangeFilters={onChangeFilters}
                    onPreviewSMS={onPreviewSMS}
                    onChangePage={onChangePage}
                    onDownloadSMSActivity={onDownloadSMSActivity}
                    handleDraftSMSClick={handleDraftSMSClick}
                    onDuplicateSMS={onDuplicateSMS}
                    onDeleteSMS={onDeleteSMS}
                    onUnsendSMS={onUnsendSMS}
                />
                <ConfirmDialog
                    title="Delete SMS"
                    open={showDeleteSMSDialog}
                    confirmLabel="DELETE"
                    confirmButtonProps={{sx: {backgroundColor: '#dc3838', color: 'white'}}}
                    denyLabel="CANCEL"
                    onConfirm={deleteSMS}
                    onDeny={resetDeleteSMSStates}
                    onClose={resetDeleteSMSStates}
                >
                    You are about to permanently delete this SMS. This action cannot be undone.
                    <br /><br />
                    Are you sure you want to proceed?
                </ConfirmDialog>
                <ConfirmDialog
                    title="Cancel Send"
                    open={showUnsendSMSDialog}
                    confirmLabel="CANCEL SEND"
                    confirmButtonProps={{sx: {backgroundColor: '#dc3838', color: 'white'}}}
                    denyLabel="CANCEL"
                    onConfirm={unsendSMS}
                    onDeny={resetUnsendSMSStates}
                    onClose={resetUnsendSMSStates}
                >
                    You are about to cancel sending this SMS. It will be moved back to drafts.
                    <br /><br />
                    Are you sure you want to proceed?
                </ConfirmDialog>
                {
                    smsToPreview &&
                    <SMSPreview
                        onClose={() => setSMSToPreview(undefined)}
                        show={!!smsToPreview}
                        sms={smsToPreview}
                    />
                }
                <LoadingOverlay absolute={true} show={isLoadingPreview} />
            </TabContent>
        </React.Fragment>
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ISMSListProps) => ({
        ...ownProps,
        tenantId: state.tenant.id,
    }),
    {
        redirectTo: push
    }
);
type IConnectedSMSListProps = ConnectedProps<typeof connector>;

export default connector(SMSList);